import createPdf, { printPdfSequence } from './createPdf';
import { BARLLENO_LOGO_PNG } from './barlleno-logo';

interface ProductsTable {
  quantity: number;
  description: string;
  priceUnity: number;
  totalLine: number;
}

interface DocumentInterface {
  date: string;
  boxName: string;
  numberTiket: number | string;
  billedBy: string;
  products: ProductsTable[];
  total: number | string;
  typePayment: string;
}

/** Para quién es cada copia del tiquete. */
export type TicketCopy = 'client' | 'internal';

/**
 * Por defecto salen dos copias, una detrás de otra: la primera se entrega
 * al cliente y la segunda se queda en caja para el control interno. Van
 * marcadas para no confundirlas. Al imprimir, cada copia es un trabajo de
 * impresión aparte, porque la impresora de tiquetes corta al final de cada
 * trabajo y no entre páginas (ver printPdfSequence).
 */
export const TICKET_COPIES: TicketCopy[] = ['client', 'internal'];

const COPY_LABEL: { [copy in TicketCopy]: string } = {
  client: 'COPIA CLIENTE',
  internal: 'CONTROL INTERNO',
};

const COPY_FOOTER: { [copy in TicketCopy]: string[] } = {
  client: ['Copia para el cliente', '¡Gracias por tu compra!'],
  internal: ['Este ticket es', 'para control interno'],
};

/** Una copia completa del tiquete; con `pageBreak` empieza en una página nueva del mismo documento. */
const ticketContent = (document: DocumentInterface, copy: TicketCopy, pageBreak: boolean = false): any[] => {
  const productRows = document.products.map(product => [
    { text: `${product.quantity}`, style: 'tProductsBody', alignment: 'left' },
    { text: `${product.description}`, style: 'tProductsBody', alignment: 'left' },
    { text: `$ ${product.priceUnity}`, style: 'tProductsBody', alignment: 'right' },
    { text: `$ ${product.totalLine}`, style: 'tProductsBody', alignment: 'right' },
  ]);

  const logo: any = {
    image: BARLLENO_LOGO_PNG, //Logo
    fit: [200, 200],
    alignment: 'center',
  };
  if (pageBreak) logo.pageBreak = 'before';

  return [
    //DATOS EMPRESA
    logo,

    //TIPO Y NUMERO DOCUMENTO
    { text: `CAJA ${document.boxName}`, style: 'header', margin: [0, 15, 0, 0] },
    // Qué copia es: se lee de un vistazo al separarlas.
    { text: `— ${COPY_LABEL[copy]} —`, style: 'header', fontSize: 10, margin: [0, 2, 0, 2] },
    {
      text: `Tiquete No. |${document.numberTiket}`,
      style: 'header',
    },
    { text: `Fecha: ${document.date}`, style: 'header' },
    { text: `Cajero: ${document.billedBy}`, style: 'header' },

    // TABLA PRODUCTOS
    {
      table: {
        widths: ['15%', '35%', '23%', '23%'],
        headerRows: 2,
        body: [
          [
            { text: 'CAN.', style: 'tProductsHeader', alignment: 'left' },
            { text: 'DESC.', style: 'tProductsHeader', alignment: 'center' },
            { text: 'P/UNI.', style: 'tProductsHeader', alignment: 'right' },
            { text: 'TOTAL', style: 'tProductsHeader', alignment: 'right' },
          ],
          ...productRows,
        ],
      },
      layout: {
        hLineWidth: function (i: any, node: any) {
          return i === 1 ? 0.5 : 0;
        },
        vLineWidth: function (i: any, node: any) {
          return 0;
        },
        hLineColor: function () {
          return '#b3b3b3';
        },
        paddingTop: function (i: any, node: any) {
          return i === 1 ? 5 : 1;
        },
      },
    },
    // TOTALES DE LA FACTURA
    {
      margin: [0, 10, 0, 0],
      table: {
        widths: ['25%', '35%', '15%', '22%'],
        body: [
          [
            {
              text: 'Total:',
              style: 'tTotals',
              colSpan: 2,
            },
            {},
            {
              text: `$ ${document.total}`,
              style: 'tTotals',
              colSpan: 2,
            },
            {},
          ],
          //FORMAS PAGO
          [
            {
              text: 'FORMA DE PAGO:',
              style: 'tTotals',
              alignment: 'left',
              margin: [10, 10, 0, 0],
              colSpan: 2,
            },
            {},
            {},
            {},
          ],
          [
            {
              text: `${document.typePayment}`,
              style: 'tProductsBody',
              colSpan: 4,
              margin: [10, 0, 0, 0],
            },
            {},
            {},
            {},
          ],
        ],
      },
      layout: 'noBorders',
    },
    ...COPY_FOOTER[copy].map(line => ({ text: line, style: 'header', margin: [0, 0, 0, 0] })),
  ];
};

/**
 * Imprime el tiquete de la orden. `copies` permite pedir una sola copia
 * (por ejemplo, solo la del cliente); por defecto salen las dos.
 *
 * Al imprimir, cada copia es un PDF y un trabajo de impresión propio, en
 * orden: así la impresora corta entre una y otra. Para las demás salidas
 * (base64) va todo en un documento, una copia por página.
 */
const generateTicket = async (output: any, document: DocumentInterface, copies: TicketCopy[] = TICKET_COPIES) => {
  const list = copies.length > 0 ? copies : TICKET_COPIES;

  if (output === 'print') {
    const docs: string[] = [];
    for (const copy of list) {
      const pdf: any = await createPdf({ content: ticketContent(document, copy) }, 'b64');
      docs.push(pdf.content);
    }
    await printPdfSequence(docs);
    return {
      success: true,
      content: null,
      message: 'Documento enviado a impresión.',
    };
  }

  const content: any[] = [];
  list.forEach((copy, index) => {
    content.push(...ticketContent(document, copy, index > 0));
  });
  const response = await createPdf({ content }, output);
  return response;
};

export default generateTicket;
